import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Chip, Skeleton, TextField, Tooltip, InputAdornment } from '@mui/material';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency, CURRENCY } from '../../config/currency';
import { AppIconButton, PrimaryButton, SecondaryButton, EmptyState } from '../ui';
import PaymentModeSelect from './PaymentModeSelect';

interface SupplierPaymentItem {
  id: number;
  expenseId: number;
  expenseDescription?: string;
  expenseAmount?: number;
  expenseDate?: string;
  expenseFromDate?: string | null;
  expenseToDate?: string | null;
  amount: number;
  paymentMode: string;
  paymentDate: string;
  notes?: string | null;
  createdAt: string;
}

interface EditPaymentDraft {
  id: number;
  amount: string;
  paymentMode: string;
  paymentDate: string;
  notes: string;
  expenseAmount: number;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank',
  UPI: 'UPI',
  CARD: 'Card',
};

const PAGE_SIZE = 10;

export interface SupplierHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierName: string;
  onPaymentChanged?: () => void; // Parent refetch payables totals after edit/delete
}

const SupplierHistoryModal: React.FC<SupplierHistoryModalProps> = ({
  isOpen,
  onClose,
  supplierName,
  onPaymentChanged,
}) => {
  const { theme } = useTheme();
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PageResponse<SupplierPaymentItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [editDraft, setEditDraft] = useState<EditPaymentDraft | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SupplierPaymentItem | null>(null);

  const fetchPage = useCallback(async (p: number) => {
    if (!supplierName) return;
    setLoading(true);
    try {
      const res = await financeApi.getSupplierPaymentHistory({
        supplier: supplierName,
        page: p,
        size: PAGE_SIZE,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      });
      setData(res.data as PageResponse<SupplierPaymentItem>);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load history';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [supplierName, dateFrom, dateTo]);

  useEffect(() => {
    if (isOpen) {
      setPage(0);
      fetchPage(0);
    }
  }, [isOpen, fetchPage]);

  useEffect(() => {
    if (isOpen) fetchPage(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditClick = (p: SupplierPaymentItem) => {
    setEditDraft({
      id: p.id,
      amount: String(p.amount),
      paymentMode: p.paymentMode,
      paymentDate: p.paymentDate,
      notes: p.notes ?? '',
      expenseAmount: p.expenseAmount ?? 0,
    });
  };

  const handleEditSave = async () => {
    if (!editDraft) return;
    const amount = Number(editDraft.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setEditSaving(true);
    try {
      await financeApi.updateSupplierPayment(editDraft.id, {
        amount,
        paymentMode: editDraft.paymentMode,
        paymentDate: editDraft.paymentDate,
        notes: editDraft.notes.trim() || null,
      });
      toast.success('Payment updated');
      setEditDraft(null);
      await fetchPage(page);
      onPaymentChanged?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update payment';
      toast.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      await financeApi.deleteSupplierPayment(pendingDelete.id);
      toast.success('Payment deleted');
      setPendingDelete(null);
      await fetchPage(page);
      onPaymentChanged?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to delete payment';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString(CURRENCY.locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return s;
    }
  };

  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  const inputSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
    },
    '& .MuiInputBase-input': { color: theme.text, fontSize: '0.813rem' },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Payment History - ${supplierName}`}
      subtitle={loading ? 'Loading...' : `${totalElements} payment${totalElements === 1 ? '' : 's'}`}
      maxWidth="md"
      footer={
        totalPages > 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <AppIconButton
                icon={<ChevronLeftIcon style={{ width: 18, height: 18 }} />}
                tooltip="Previous"
                variant="secondary"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              />
              <AppIconButton
                icon={<ChevronRightIcon style={{ width: 18, height: 18 }} />}
                tooltip="Next"
                variant="secondary"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
              />
            </Box>
          </Box>
        ) : null
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Date filters */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type="date"
            label="From"
            size="small"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
          <TextField
            type="date"
            label="To"
            size="small"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        </Box>

        {/* List */}
        {loading && !data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: '12px' }} />
            ))}
          </Box>
        )}

        {!loading && data && data.content.length === 0 && (
          <EmptyState
            icon={<DocumentTextIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
            title="No payments to this supplier in this range"
            subtitle="Try widening the dates, or record a payment from the Payables page — it will show up here."
          />
        )}

        {data && data.content.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.content.map((p) => (
              <Box
                key={p.id}
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                  opacity: loading ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>
                      {formatDate(p.paymentDate)}
                    </Typography>
                    {p.expenseDescription && (
                      <Typography variant="caption" noWrap sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.7rem', display: 'block' }}>
                        {p.expenseDescription}
                      </Typography>
                    )}
                    {p.expenseFromDate && p.expenseToDate && p.expenseFromDate !== p.expenseToDate && (
                      <Tooltip
                        title={`Covers ${formatDate(p.expenseFromDate)} → ${formatDate(p.expenseToDate)}`}
                        arrow
                        placement="bottom-start"
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.primary,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            display: 'block',
                            mt: 0.25,
                            cursor: 'help',
                          }}
                        >
                          {formatDate(p.expenseFromDate)} → {formatDate(p.expenseToDate)}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: theme.success || '#4CAF50', fontSize: '1rem', flexShrink: 0, ml: 1 }}>
                    {formatCurrency(p.amount, { showDecimals: false })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={PAYMENT_MODE_LABELS[p.paymentMode] || p.paymentMode}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.6rem',
                      height: 18,
                      backgroundColor: theme.gray?.[100] || '#F0F0F0',
                      color: theme.gray?.[500] || '#666',
                    }}
                  />
                  {p.expenseAmount != null && (
                    <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.65rem' }}>
                      Expense: {formatCurrency(p.expenseAmount, { showDecimals: false })}
                    </Typography>
                  )}
                  {p.notes && (
                    <Typography variant="caption" noWrap sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.65rem', fontStyle: 'italic', flex: 1, minWidth: 0 }}>
                      {p.notes}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                    <AppIconButton
                      icon={<PencilSquareIcon style={{ width: 14, height: 14 }} />}
                      tooltip="Edit payment"
                      variant="ghost"
                      size="small"
                      onClick={() => handleEditClick(p)}
                      data-testid={`payment-edit-${p.id}`}
                    />
                    <AppIconButton
                      icon={<TrashIcon style={{ width: 14, height: 14 }} />}
                      tooltip="Delete payment"
                      variant="ghost"
                      size="small"
                      onClick={() => setPendingDelete(p)}
                      disabled={deletingId === p.id}
                      sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                      data-testid={`payment-delete-${p.id}`}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Edit Payment Modal */}
      <Modal
        isOpen={Boolean(editDraft)}
        onClose={() => setEditDraft(null)}
        title="Edit Payment"
        maxWidth="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <SecondaryButton onClick={() => setEditDraft(null)} disabled={editSaving} fullWidth>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleEditSave}
              disabled={editSaving || !editDraft?.amount || Number(editDraft?.amount) <= 0}
              loading={editSaving}
              fullWidth
            >
              Save Changes
            </PrimaryButton>
          </Box>
        }
      >
        {editDraft && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 0, sm: 1 } }}>
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.7rem' }}>
              Expense total: {formatCurrency(editDraft.expenseAmount, { showDecimals: false })} · Edits adjust the payable's running balance.
            </Typography>

            <TextField
              label="Payment Amount *"
              type="number"
              value={editDraft.amount}
              onChange={(e) => setEditDraft({ ...editDraft, amount: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600 }}>{CURRENCY.symbol}</Typography>
                  </InputAdornment>
                ),
                inputProps: { min: 0, step: 1, style: { fontSize: '1.25rem', fontWeight: 700 } },
              }}
              fullWidth
            />

            <PaymentModeSelect
              value={editDraft.paymentMode}
              onChange={(v) => setEditDraft({ ...editDraft, paymentMode: v })}
              label="Payment Mode"
              fullWidth
            />

            <TextField
              label="Date"
              type="date"
              value={editDraft.paymentDate}
              onChange={(e) => setEditDraft({ ...editDraft, paymentDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Notes"
              multiline
              minRows={2}
              maxRows={3}
              value={editDraft.notes}
              onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
              fullWidth
            />
          </Box>
        )}
      </Modal>

      {/* Delete Payment Confirm */}
      <Modal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title="Delete Payment?"
        maxWidth="xs"
        footer={
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <SecondaryButton onClick={() => setPendingDelete(null)} disabled={deletingId !== null} fullWidth>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleDeleteConfirm}
              disabled={deletingId !== null}
              loading={deletingId !== null}
              fullWidth
              sx={{ backgroundColor: `${theme.danger || '#D99982'} !important`, '&:hover': { backgroundColor: `${theme.danger || '#D99982'}DD !important` } }}
            >
              Delete
            </PrimaryButton>
          </Box>
        }
      >
        {pendingDelete && (
          <Box sx={{ p: 1 }}>
            <Typography sx={{ color: theme.text, fontSize: '0.95rem', mb: 1 }}>
              Remove this payment of{' '}
              <Typography component="span" sx={{ fontWeight: 700, color: theme.danger || '#D99982' }}>
                {formatCurrency(pendingDelete.amount, { showDecimals: false })}
              </Typography>{' '}
              on {formatDate(pendingDelete.paymentDate)}?
            </Typography>
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.75rem' }}>
              Payable's paid total will decrease by this amount. Cannot be undone.
            </Typography>
          </Box>
        )}
      </Modal>
    </Modal>
  );
};

export default SupplierHistoryModal;
